import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Input from '../../components/input/Input';
import CurrencyInput from '../../components/input/CurrencyInput';
import DatePicker from '../../components/datepicker/DatePicker';
import DropDown from '../../components/dropdown/DropDown';
import TextArea from '../../components/textarea/TextArea';
import MontoTooltipLabel from '../../components/labels/MontoTooltipLabel';
import { MonetizationFormData, FormValues } from '../../pages/MonetizationNew';
import { calculateTermInDays } from '../../utils/dateCalculations';
import { useEffect, useState } from 'react';
import AssetSelectionModal from '../../components/modals/AssetSelectionModal';
import OtrasDivisasButton from '../../components/monetization/OtrasDivisasButton';
import AssetLoadedBanner from '../../components/monetization/AssetLoadedBanner';
import { getQuotations } from '../../services/QuotationsService';
import ManualInstallmentPlanSection, {
  type ManualInstallmentPlanSectionProps,
} from './ManualInstallmentPlanSection';
import {
  AMORTIZATION_TYPE_OTRO,
  PAYMENT_FREQUENCY_OTRO,
  isOtroAmortization,
  isOtroPaymentFrequency,
  requiresManualInstallmentPlan,
} from '../../utils/loanInstallmentPlanConstants';

const loanDataGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '16px',
  alignItems: 'end' as const,
};

interface LoanSectionProps {
  formData: MonetizationFormData;
  formValues: FormValues;
  updateFormValues: (updates: Partial<FormValues>) => void;
  operationType?: string;
  amountInputStatus?: 'normal' | 'error';
  amountInputMessage?: string;
  currencyCode?: string;
  usdQuotation?: number | null;
  amountReadOnly?: boolean;
  showManualInstallmentPlan?: boolean;
  manualInstallmentPlan?: ManualInstallmentPlanSectionProps | null;
}

const LoanSection = ({
  formData,
  formValues,
  updateFormValues,
  operationType,
  amountInputStatus = 'normal',
  amountInputMessage,
  currencyCode = 'ARS',
  usdQuotation,
  amountReadOnly = false,
  showManualInstallmentPlan = false,
  manualInstallmentPlan = null,
}: LoanSectionProps) => {
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assetName, setAssetName] = useState('');
  const [otroLockedField, setOtroLockedField] = useState<'amortization' | 'frequency' | null>(() => {
    if (isOtroAmortization(formValues.amortization_type_id)) return 'frequency';
    if (isOtroPaymentFrequency(formValues.payment_frequency_type_id)) return 'amortization';
    return null;
  });
  const hasAsset = !!formValues.asset_id;
  const showLoadButton = !hasAsset && !amountReadOnly;
  useEffect(() => {
    if (formValues.issue_date && formValues.due_date) {
      const [iy, im, id] = formValues.issue_date.split('-').map(Number);
      const issue = new Date(iy, im - 1, id);
      const [dy, dm, dd] = formValues.due_date.split('-').map(Number);
      const due = new Date(dy, dm - 1, dd);

      const minDue = new Date(issue);
      const grace = formValues.grace_period;
      if (grace != null && grace > 0) {
        minDue.setDate(minDue.getDate() + grace);
      }

      if (due < minDue) {
        if (formValues.due_date !== '' || formValues.term_in_days !== 0) {
          updateFormValues({ due_date: '', term_in_days: 0 });
        }
        return;
      }
      const termInDays = calculateTermInDays(formValues.issue_date, formValues.due_date);
      const currentTerm = formValues.term_in_days == null ? null : Number(formValues.term_in_days);
      if (currentTerm !== termInDays) {
        updateFormValues({ term_in_days: termInDays });
      }
    }
  }, [formValues.issue_date, formValues.due_date, formValues.grace_period, formValues.term_in_days, updateFormValues]);

  const creditorItems = formData.creditors?.map(creditor => ({
    label: creditor.name,
    disabled: false
  })) || [];

  const amortizationItems = formData.amortization_types?.map(type => ({
    label: type.name,
    disabled: false
  })) || [];

  const paymentFrequencyItems = formData.payment_frequency_types?.map(type => ({
    label: type.name,
    disabled: false
  })) || [];

  const rateTypeItems = formData.rate_types?.map(type => ({
    label: type.name,
    disabled: false
  })) || [];

  const purposeTypeItems = formData.purpose_types?.map(type => ({
    label: type.name,
    disabled: false
  })) || [];

  const handleCreditorChange = (selectedLabel: string) => {
    const creditor = formData.creditors?.find(c => c.name === selectedLabel);
    if (creditor) {
      updateFormValues({ creditor_id: creditor.id });
    }
  };

  const resolveOtroAmortizationId = () =>
    formData.amortization_types?.find((type) => type.id === AMORTIZATION_TYPE_OTRO)?.id ??
    formData.amortization_types?.find((type) => type.name.toLowerCase() === 'otro')?.id ??
    AMORTIZATION_TYPE_OTRO;

  const resolveOtroFrequencyId = () =>
    formData.payment_frequency_types?.find((type) => type.id === PAYMENT_FREQUENCY_OTRO)?.id ??
    formData.payment_frequency_types?.find((type) => type.name.toLowerCase() === 'otro')?.id ??
    PAYMENT_FREQUENCY_OTRO;

  const handleAmortizationChange = (selectedLabel: string) => {
    const amortization = formData.amortization_types?.find((a) => a.name === selectedLabel);
    if (!amortization) return;

    const updates: Partial<FormValues> = { amortization_type_id: amortization.id };
    if (isOtroAmortization(amortization.id)) {
      updates.payment_frequency_type_id = resolveOtroFrequencyId();
      setOtroLockedField('frequency');
    } else {
      if (otroLockedField === 'frequency') {
        setOtroLockedField(null);
      }
      if (isOtroPaymentFrequency(formValues.payment_frequency_type_id)) {
        updates.payment_frequency_type_id = null;
      }
    }
    updateFormValues(updates);
  };

  const handlePaymentFrequencyChange = (selectedLabel: string) => {
    const paymentFreq = formData.payment_frequency_types?.find((p) => p.name === selectedLabel);
    if (!paymentFreq) return;

    const updates: Partial<FormValues> = { payment_frequency_type_id: paymentFreq.id };
    if (isOtroPaymentFrequency(paymentFreq.id)) {
      updates.amortization_type_id = resolveOtroAmortizationId();
      setOtroLockedField('amortization');
    } else {
      if (otroLockedField === 'amortization') {
        setOtroLockedField(null);
      }
      if (isOtroAmortization(formValues.amortization_type_id)) {
        updates.amortization_type_id = null;
      }
    }
    updateFormValues(updates);
  };

  const handleRateTypeChange = (selectedLabel: string) => {
    const rateType = formData.rate_types?.find(r => r.name === selectedLabel);
    if (rateType) {
      updateFormValues({ rate_type_id: rateType.id });
    }
  };

  const handlePurposeTypeChange = (selectedLabel: string) => {
    const purposeType = formData.purpose_types?.find(p => p.name === selectedLabel);
    if (purposeType) {
      updateFormValues({ purpose_type_id: purposeType.id });
    }
  };

  const handleObservationChange = (e: { target: { value: string } }) => {
    const value = e.target.value;
    if (value.length <= 2500) {
      updateFormValues({ observation: value });
    }
  };

  // Helper functions to get selected values for dropdowns
  const getSelectedCreditor = () => {
    const creditor = formData.creditors?.find(c => c.id === formValues.creditor_id);
    return creditor?.name || 'Seleccionar';
  };

  const getSelectedAmortization = () => {
    const amortization = formData.amortization_types?.find(a => a.id === formValues.amortization_type_id);
    return amortization?.name || 'Seleccionar';
  };

  const getSelectedPaymentFrequency = () => {
    const paymentFreq = formData.payment_frequency_types?.find(p => p.id === formValues.payment_frequency_type_id);
    return paymentFreq?.name || 'Seleccionar';
  };

  const getSelectedRateType = () => {
    const rateType = formData.rate_types?.find(r => r.id === formValues.rate_type_id);
    return rateType?.name || 'Seleccionar tipo de tasa';
  };

  const getSelectedPurposeType = () => {
    const purposeType = formData.purpose_types?.find(p => p.id === formValues.purpose_type_id);
    return purposeType?.name || 'Seleccionar';
  };

  useEffect(() => {
    if (!formValues.asset_id || assetName) return;
    getQuotations()
      .then((response) => {
        if (response.success && response.data && Array.isArray(response.data)) {
          const asset = (response.data as Array<{ id: number; name: string }>).find(
            (a) => a.id === formValues.asset_id
          );
          if (asset) setAssetName(asset.name);
        }
      })
      .catch(() => {});
  }, [formValues.asset_id, assetName]);

  const handleAssetAccept = (data: {
    assetId: number;
    assetName: string;
    quantity: number;
    quotation: number;
    calculatedAmount: number;
  }) => {
    setAssetName(data.assetName);
    updateFormValues({
      asset_id: data.assetId,
      asset_quantity: data.quantity,
      asset_quotation: data.quotation,
      monetization_amount: data.calculatedAmount,
    });
    setAssetModalOpen(false);
  };

  const handleAssetClear = () => {
    setAssetName('');
    updateFormValues({
      asset_id: null,
      asset_quantity: null,
      asset_quotation: null,
      monetization_amount: null,
    });
  };

  return (
    <>
      {/* Datos del crédito */}
      <Card margin={{ bottom: '4' }}>
        <div style={{ backgroundColor: '#E6F5F0', padding: '12px 16px', borderRadius: '8px', margin: '0 0 16px 0' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong' withMargin={false}>
            Datos del crédito
          </H>
        </div>
        
        {/* Fila 1: Monto bruto | Divisa + Otras divisas | Fecha emisión | Fecha vencimiento */}
        <div
          data-testid="loan-data-row-1"
          style={{ ...loanDataGridStyle, marginBottom: hasAsset ? '8px' : '24px' }}
        >
          <Card>
            <MontoTooltipLabel />
            <Card margin={{ top: '1' }}>
              <CurrencyInput
                value={formValues.monetization_amount}
                onValueChange={(val) => updateFormValues({ monetization_amount: val })}
                size="md"
                modifier={hasAsset || amountReadOnly ? 'weak' : undefined}
                inputStatus={hasAsset || amountReadOnly ? 'normal' : amountInputStatus}
                message={hasAsset || amountReadOnly ? undefined : amountInputMessage}
                readOnly={hasAsset || amountReadOnly}
              />
            </Card>
          </Card>
          <div style={{ display: 'flex', alignItems: 'end', gap: '8px', minWidth: 0 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input
                label="Divisa"
                value={formData.currency}
                readOnly
                size="md"
                modifier="weak"
              />
            </div>
            {showLoadButton && (
              <OtrasDivisasButton onClick={() => setAssetModalOpen(true)} />
            )}
          </div>
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de emisión *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.issue_date}
                onChange={(date: string) => updateFormValues({ issue_date: date })}
                disabled={false}
                displayFormat="DD/MM/AA"
              />
            </Card>
          </Card>
          <Card>
            <P withMargin={false} variant='neutral' type='4' modifier='strong'>
              Fecha de vencimiento *
            </P>
            <Card margin={{ top: '2' }}>
              <DatePicker
                defaultValue={formValues.due_date}
                onChange={(date: string) => updateFormValues({ due_date: date })}
                disabled={false}
                displayFormat="DD/MM/AA"
                minDate={(() => {
                  if (!formValues.issue_date) return undefined;
                  const [y, m, d] = formValues.issue_date.split('-').map(Number);
                  const base = new Date(y, m - 1, d);
                  const grace = formValues.grace_period;
                  if (grace != null && grace > 0) {
                    base.setDate(base.getDate() + grace);
                  }
                  return base;
                })()}
              />
            </Card>
          </Card>
        </div>

        {hasAsset && (
          <div style={{ marginBottom: '24px' }}>
            <AssetLoadedBanner
              assetName={assetName}
              quantity={formValues.asset_quantity ?? 0}
              quotation={formValues.asset_quotation ?? 0}
              calculatedAmount={formValues.monetization_amount ?? 0}
              currencyCode={currencyCode}
              onEdit={() => setAssetModalOpen(true)}
              onClear={handleAssetClear}
            />
          </div>
        )}

        {/* Fila 2 */}
        <Card margin={{ top: '3' }}>
          <div data-testid="loan-data-row-2" style={{ ...loanDataGridStyle }}>
            <DropDown
              id="creditor"
              label="Banco acreedor *"
              title={getSelectedCreditor()}
              items={creditorItems}
              onChange={handleCreditorChange}
              size="md"
              marginTop="4px"
            />
            <Input
              label="Nro de operación banco"
              value={formValues.bank_operation_number || ''}
              onChange={(e) => updateFormValues({ bank_operation_number: e.target.value })}
              size="md"
              maxLength={250}
              inputStatus="normal"
              readOnly={operationType === 'Operaciones Públicas'}
              modifier={operationType === 'Operaciones Públicas' ? 'weak' : undefined}
            />
            <div>
              <DropDown
                id="amortization"
                label="Sistema de amortización *"
                title={getSelectedAmortization()}
                items={amortizationItems}
                onChange={handleAmortizationChange}
                size="md"
                marginTop="4px"
                disabled={otroLockedField === 'amortization'}
              />
              {otroLockedField === 'amortization' ? (
                <P type="4" variant="neutral" withMargin={false} data-testid="otro-lock-help-amortization">
                  Con periodicidad OTRO, el sistema de amortización debe ser OTRO.
                </P>
              ) : null}
            </div>
            <div>
              <DropDown
                id="payment_frequency"
                label="Periodicidad de pagos *"
                title={getSelectedPaymentFrequency()}
                items={paymentFrequencyItems}
                onChange={handlePaymentFrequencyChange}
                size="md"
                marginTop="4px"
                disabled={otroLockedField === 'frequency'}
              />
              {otroLockedField === 'frequency' ? (
                <P type="4" variant="neutral" withMargin={false} data-testid="otro-lock-help-frequency">
                  Con amortización OTRO, la periodicidad debe ser OTRO.
                </P>
              ) : null}
            </div>
          </div>
        </Card>

        {/* Fila 3: Plazo y Destino alineados con columnas 1 y 2 */}
        <Card margin={{ top: '3' }}>
          <div data-testid="loan-data-row-3" style={{ ...loanDataGridStyle }}>
            <Input
              label="Plazo en días *"
              value={calculateTermInDays(formValues.issue_date || '', formValues.due_date || '').toString()}
              readOnly
              size="md"
              modifier="weak"
            />
            <DropDown
              id="purpose_type"
              label="Destino *"
              title={getSelectedPurposeType()}
              items={purposeTypeItems}
              onChange={handlePurposeTypeChange}
              size="md"
              marginTop="4px"
            />
          </div>
        </Card>
      </Card>

      {showManualInstallmentPlan &&
      manualInstallmentPlan &&
      requiresManualInstallmentPlan(
        formValues.amortization_type_id,
        formValues.payment_frequency_type_id
      ) ? (
        <ManualInstallmentPlanSection {...manualInstallmentPlan} />
      ) : null}

      {/* Tasas y gracia */}
      <Card margin={{ bottom: '4' }}>
        <div style={{ backgroundColor: '#E6F5F0', padding: '12px 16px', borderRadius: '8px', margin: '0 0 16px 0' }}>
          <H variant="secondary-2" type='4' weight='bold' modifier='strong' withMargin={false}>
            Tasas y gracia
          </H>
        </div>
        
        <div data-testid="loan-rates-row-1" style={{ ...loanDataGridStyle, marginBottom: '24px' }}>
          <DropDown
            id="rate_type"
            label="Tasa *"
            title={getSelectedRateType()}
            items={rateTypeItems}
            onChange={handleRateTypeChange}
            size="md"
            marginTop="4px"
          />
          <Input
            label="Puntos Porcentuales *"
            type="number"
            value={formValues.percentage_point?.toString() || ''}
            onChange={(e) => updateFormValues({ percentage_point: parseFloat(e.target.value) || null })}
            size="md"
            inputStatus="normal"
          />
          <Input
            label="Periodo de Gracia *"
            type="number"
            value={formValues.grace_period?.toString() ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                updateFormValues({ grace_period: null });
                return;
              }
              const parsed = parseInt(raw, 10);
              if (Number.isNaN(parsed) || parsed < 0) return;
              updateFormValues({ grace_period: parsed });
            }}
            size="md"
            onlyNumbers
            inputStatus="normal"
          />
        </div>
        <div data-testid="loan-rates-row-2">
          <TextArea
            label="Observación"
            value={formValues.observation || ''}
            onChange={handleObservationChange}
            placeHolder="Ingrese observaciones adicionales (opcional)"
            maxLength={2500}
            rows={4}
            maxLengthHelpText={true}
          />
        </div>
      </Card>

      <AssetSelectionModal
        open={assetModalOpen}
        onClose={() => setAssetModalOpen(false)}
        onAccept={handleAssetAccept}
        currencyCode={currencyCode}
        usdQuotation={usdQuotation}
        initialAssetId={formValues.asset_id}
        initialQuantity={formValues.asset_quantity}
        initialQuotation={formValues.asset_quotation}
      />
    </>
  );
};

export default LoanSection;